import type { MouseEvent } from 'react';
import type { Contract } from '@entities/legal/types';
import { contractStatusLabel } from '@shared/lib/statusLabels';
import { AnalyzingBadge } from '@shared/ui/AnalyzingBadge';
import { Badge } from '@shared/ui/Badge';
import { Card } from '@shared/ui/Card';
import { Loader } from '@shared/ui/Progress';
import { Table } from '@shared/ui/Table';
import { archiveInfo, formatDate } from './constants';
import {
  ArchiveWhen,
  ContractNumber,
  ContractRow,
  CounterpartyCell,
  Empty,
  RestoreBtn,
  ThSortable,
} from './styles';

interface ContractsTableProps {
  items: Contract[];
  isLoading: boolean;
  isArchive: boolean;
  sort: string;
  order: 'asc' | 'desc';
  canWrite: boolean;
  isRestoring: boolean;
  emptyMessage: string;
  onSort: (field: string) => void;
  onRowClick: (id: string) => void;
  onRestore: (e: MouseEvent, id: string) => void;
}

export function ContractsTable({
  items,
  isLoading,
  isArchive,
  sort,
  order,
  canWrite,
  isRestoring,
  emptyMessage,
  onSort,
  onRowClick,
  onRestore,
}: ContractsTableProps) {
  function sortArrow(field: string): string {
    if (sort !== field) return '';
    return order === 'asc' ? ' ↑' : ' ↓';
  }

  return (
    <Card>
      <Table>
        <thead>
          <tr>
            <ThSortable
              $active={sort === 'counterparty'}
              onClick={() => onSort('counterparty')}
            >
              Контрагент{sortArrow('counterparty')}
            </ThSortable>
            <th>Предмет</th>
            <ThSortable $active={sort === 'sum'} onClick={() => onSort('sum')}>
              Сумма{sortArrow('sum')}
            </ThSortable>
            <th>Подписан</th>
            <ThSortable $active={sort === 'expires_at'} onClick={() => onSort('expires_at')}>
              Истекает{sortArrow('expires_at')}
            </ThSortable>
            <ThSortable $active={sort === 'status'} onClick={() => onSort('status')}>
              Статус{sortArrow('status')}
            </ThSortable>
            {isArchive && (
              <>
                <th>В архиве</th>
                <th aria-label="Действие" />
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <Empty colSpan={isArchive ? 8 : 6}>
                <Loader />
              </Empty>
            </tr>
          ) : items.length === 0 ? (
            <tr>
              <Empty colSpan={isArchive ? 8 : 6}>{emptyMessage}</Empty>
            </tr>
          ) : (
            items.map((c) => (
              <ContractRow
                key={c.id}
                onClick={() => onRowClick(c.id)}
              >
                <td>
                  <CounterpartyCell>
                    <span>{c.counterparty}</span>
                    {c.contract_number && (
                      <ContractNumber>№ {c.contract_number}</ContractNumber>
                    )}
                  </CounterpartyCell>
                </td>
                <td>{c.subject ?? '—'}</td>
                <td>
                  {c.sum != null ? `${c.sum.toLocaleString('ru-RU')} ${c.currency ?? ''}` : '—'}
                </td>
                <td>{formatDate(c.signed_at)}</td>
                <td>{formatDate(c.expires_at)}</td>
                <td>
                  {c.status === 'under_review' ? (
                    <AnalyzingBadge />
                  ) : (
                    <Badge>{contractStatusLabel(c.status)}</Badge>
                  )}
                </td>
                {isArchive &&
                  (() => {
                    const info = archiveInfo(c);
                    return (
                      <>
                        <td>
                          <Badge>{info.reason}</Badge>
                          <ArchiveWhen>{info.when}</ArchiveWhen>
                        </td>
                        <td>
                          <RestoreBtn
                            type="button"
                            onClick={(e) => onRestore(e, c.id)}
                            disabled={!canWrite || isRestoring}
                            title={!canWrite ? 'Только чтение' : 'Вернуть в работу'}
                          >
                            Вернуть
                          </RestoreBtn>
                        </td>
                      </>
                    );
                  })()}
              </ContractRow>
            ))
          )}
        </tbody>
      </Table>
    </Card>
  );
}
